import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { FullCreditApplication, RequiredExternalStep, RequiredExternalStepDto } from '../../types'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'
import transformFullCreditApplicationFromApi from '../full-credit-application/full-credit-application-transform'

const baseUrl = '/Applications/{financingProgramId}/{creditApplicationId}/BankAccountRequest'

// CREATE OPERATION

const createBankAccountRequest: MutationFunction<RequiredExternalStep, Partial<RequiredExternalStepDto>> = async (
  data,
) => {
  const apiClient = getApiClient()
  const url = baseUrl
    .replace('{financingProgramId}', data.financingProgramId ?? '')
    .replace('{creditApplicationId}', data.creditApplicationId ?? '')
  const response = await apiClient.post<RequiredExternalStep>(url, data)
  return response.data
}

export function useCreateBankAccountRequest(): [
  MutationFunction<RequiredExternalStep, Partial<RequiredExternalStepDto>>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: createBankAccountRequest,
    onSuccess: (createdStep, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId!, params.financingProgramId!)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          requiredExternalSteps: [...currentData.requiredExternalSteps, createdStep],
        })
      }
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

// RESEND URL OPERATION

const resendUrlForBankAccountRequest: MutationFunction<RequiredExternalStep, Partial<RequiredExternalStepDto>> = async (
  data,
) => {
  const apiClient = getApiClient()
  const url = baseUrl
    .replace('{financingProgramId}', data.financingProgramId ?? '')
    .replace('{creditApplicationId}', data.creditApplicationId ?? '')
    .concat('/ResendUrl')
  const response = await apiClient.post<RequiredExternalStep>(url, data)
  return response.data
}

export function useResendUrlForBankAccountRequest(): [
  MutationFunction<RequiredExternalStep, Partial<RequiredExternalStepDto>>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: resendUrlForBankAccountRequest,
    onSuccess: (updatedStep, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId!, params.financingProgramId!)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          requiredExternalSteps: currentData.requiredExternalSteps.map((s) =>
            s.id === updatedStep.id ? updatedStep : s,
          ),
        })
      }
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

// RESET OPERATION

const resetPersonalLoanBankAccountRequest: MutationFunction<
  FullCreditApplication,
  Partial<RequiredExternalStepDto>
> = async (data) => {
  const apiClient = getApiClient()
  const url = baseUrl
    .replace('{financingProgramId}', data.financingProgramId ?? '')
    .replace('{creditApplicationId}', data.creditApplicationId ?? '')
    .concat('/reset')
  const response = await apiClient.put<unknown>(url, data)
  return transformFullCreditApplicationFromApi(
    response.data as Parameters<typeof transformFullCreditApplicationFromApi>[0],
  )
}

export function useResetPersonalLoanBankAccountRequest(): [
  MutationFunction<FullCreditApplication, Partial<RequiredExternalStepDto>>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: resetPersonalLoanBankAccountRequest,
    onSuccess: (fullCreditApp, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId!, params.financingProgramId!)
      queryClient.setQueryData<FullCreditApplication>(queryKey, fullCreditApp)
    },
  })

  return [mutateAsync, isPending, isError, reset]
}
